import React from 'react';

/**
 * One numbered step of a derivation: a pill, the formula in words, and the
 * arithmetic on a ruled rail beneath it.
 *
 * The pill and rail class strings live here and nowhere else — they were
 * byte-identical in four copies, which is what made them worth extracting.
 *
 * Consumers that stack their steps inside an already-`text-xs` panel pass
 * `className={null}` and get no wrapper element.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.pill - "Step 1", already translated
 * @param {React.ReactNode} props.label - the formula in words
 * @param {string|null} [props.className] - wrapper classes, or null for none
 * @param {string} [props.headerClassName]
 * @param {string} [props.pillClassName] - defaults to `BREAKDOWN_STEP_PILL`
 * @param {React.ReactNode} props.children - the rail's contents
 */
export const BREAKDOWN_STEP_PILL =
  'bg-purple-200 text-purple-800 px-1.5 py-0.5 rounded text-[10px] font-semibold';

/**
 * The pill, told not to wrap.
 *
 * A pill is a LABEL, not a paragraph. As a flex child with neither
 * `whitespace-nowrap` nor `shrink-0` it shrinks to fit whatever the formula
 * beside it leaves over, and "Step 2" breaks across two lines, one word each.
 * That only shows once a step's label is long enough to squeeze it, which is a
 * property of the label rather than of the pill — so the fix is opt-in on the
 * consumers whose labels are long, and `BREAKDOWN_STEP_PILL` above is
 * byte-identical to what it has always been.
 *
 * It is not the default because four EU-side tooltips pin their rendered DOM
 * class-for-class, and a class string that changes is a rendered output that
 * changed (I1) — even when it cannot alter a single pixel of what those
 * tooltips draw.
 */
export const BREAKDOWN_STEP_PILL_NOWRAP = `${BREAKDOWN_STEP_PILL} whitespace-nowrap shrink-0`;

/** The header, with the pill pinned to the first line of a label that wraps. */
export const BREAKDOWN_STEP_HEADER_TOP = 'flex items-start gap-1 text-gray-600 mb-1';

export const BREAKDOWN_STEP_RAIL = 'pl-2 border-l-2 border-purple-200 ml-1';

export default function CgBreakdownStep({
  pill,
  label,
  className = 'text-xs',
  headerClassName = 'flex items-center gap-1 text-gray-600 mb-1',
  // The pill's classes. Defaults to the bare constant, which is what every
  // caller rendered before a long label made the pill wrap — see
  // `BREAKDOWN_STEP_PILL_NOWRAP`.
  pillClassName = BREAKDOWN_STEP_PILL,
  children,
}) {
  const body = (
    <>
      <div className={headerClassName}>
        <span className={pillClassName}>{pill}</span>
        <span>{label}</span>
      </div>
      <div className={BREAKDOWN_STEP_RAIL}>{children}</div>
    </>
  );

  return className ? <div className={className}>{body}</div> : body;
}
